import { useState, useEffect, useCallback } from 'react'

export interface Comment {
  id: string
  note_id: string
  user_id: string
  author: string
  parent_id: string | null
  anchor_text: string
  body: string
  resolved: boolean
  resolved_at: string | null
  created_at: string
  updated_at: string
  replies: Comment[]
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options?.headers,
    },
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  if (response.status === 204) return null as T
  return response.json()
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

export function useComments(noteSlug: string | undefined) {
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(false)

  const fetchComments = useCallback(async () => {
    if (!noteSlug) return
    setLoading(true)
    try {
      const data = await fetchJson<Comment[]>(`/notes/${noteSlug}/comments`)
      setComments(data)
    } catch (e) {
      console.error('Failed to fetch comments:', e)
    } finally {
      setLoading(false)
    }
  }, [noteSlug])

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const addComment = useCallback(async (anchorText: string, body: string): Promise<Comment | null> => {
    if (!noteSlug) return null
    try {
      const comment = await fetchJson<Comment>(`/notes/${noteSlug}/comments`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ comment: { anchor_text: anchorText, body } }),
      })
      setComments(prev => [...prev, comment])
      return comment
    } catch (e) {
      console.error('Failed to add comment:', e)
      return null
    }
  }, [noteSlug])

  const replyToComment = useCallback(async (commentId: string, body: string): Promise<Comment | null> => {
    try {
      const reply = await fetchJson<Comment>(`/comments/${commentId}/replies`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ comment: { body } }),
      })
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, replies: [...c.replies, reply] } : c
      ))
      return reply
    } catch (e) {
      console.error('Failed to reply:', e)
      return null
    }
  }, [])

  const resolveComment = useCallback(async (commentId: string) => {
    try {
      await fetchJson<Comment>(`/comments/${commentId}/resolve`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': csrfToken() },
      })
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, resolved: true, resolved_at: new Date().toISOString() } : c
      ))
    } catch (e) {
      console.error('Failed to resolve:', e)
    }
  }, [])

  const unresolveComment = useCallback(async (commentId: string) => {
    try {
      await fetchJson<Comment>(`/comments/${commentId}/unresolve`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': csrfToken() },
      })
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, resolved: false, resolved_at: null } : c
      ))
    } catch (e) {
      console.error('Failed to unresolve:', e)
    }
  }, [])

  const deleteComment = useCallback(async (commentId: string) => {
    try {
      await fetchJson<null>(`/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken() },
      })
      setComments(prev => prev.filter(c => c.id !== commentId))
    } catch (e) {
      console.error('Failed to delete:', e)
    }
  }, [])

  return {
    comments,
    loading,
    addComment,
    replyToComment,
    resolveComment,
    unresolveComment,
    deleteComment,
    refetch: fetchComments,
  }
}
